import * as React from 'react';
import { useSearchParams } from 'react-router';

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const useUrlParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = React.useCallback(
    (params: Record<string, unknown>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        Object.entries(params).forEach(([key, value]) => {
          // Skip empty values
          if (isEmpty(value)) {
            // Remove the param if it exists
            if (key === 'filters') {
              // Remove all filter params
              const keysToRemove: string[] = [];
              newParams.forEach((_, paramKey) => {
                if (paramKey.startsWith('filters[')) {
                  keysToRemove.push(paramKey);
                }
              });
              keysToRemove.forEach((k) => newParams.delete(k));
            } else if (key.startsWith('filters[')) {
              // Remove all filter params with this key pattern
              const filterKey = key.match(/^filters\[([^\]]+)\]/)?.[1];
              if (filterKey) {
                // Remove all params that match filters[filterKey] or filters[filterKey][]
                const keysToRemove: string[] = [];
                newParams.forEach((_, paramKey) => {
                  if (
                    paramKey === `filters[${filterKey}]` ||
                    paramKey === `filters[${filterKey}][]` ||
                    paramKey.startsWith(`filters[${filterKey}][`)
                  ) {
                    keysToRemove.push(paramKey);
                  }
                });
                keysToRemove.forEach((k) => newParams.delete(k));
              }
            } else {
              // Remove regular param and array variants
              newParams.delete(key);
              newParams.delete(`${key}[]`);
            }
            return;
          }

          // Handle filters object (nested structure)
          if (
            key === 'filters' &&
            typeof value === 'object' &&
            !Array.isArray(value)
          ) {
            // Remove all existing filter params first
            const keysToRemove: string[] = [];
            newParams.forEach((_, paramKey) => {
              if (paramKey.startsWith('filters[')) {
                keysToRemove.push(paramKey);
              }
            });
            keysToRemove.forEach((k) => newParams.delete(k));

            // Add new filter params
            Object.entries(value as Record<string, unknown>).forEach(
              ([filterKey, filterValue]) => {
                if (isEmpty(filterValue)) return;

                if (Array.isArray(filterValue)) {
                  // Array filters: filters[key][]=value1&filters[key][]=value2
                  filterValue.forEach((v) => {
                    newParams.append(`filters[${filterKey}][]`, String(v));
                  });
                } else {
                  // Single value filters: filters[key]=value
                  newParams.set(`filters[${filterKey}]`, String(filterValue));
                }
              }
            );
            return;
          }

          // Handle arrays (non-filter arrays)
          if (Array.isArray(value)) {
            // Remove existing params with this key
            newParams.delete(key);
            newParams.delete(`${key}[]`);
            // Append each array item
            value.forEach((v) => {
              newParams.append(`${key}[]`, String(v));
            });
            return;
          }

          // Handle primitives - skip empty strings
          if (value === '') {
            newParams.delete(key);
            return;
          }

          newParams.set(key, String(value));
        });

        return newParams;
      });
    },
    [setSearchParams]
  );

  /**
   * Parses filters from URL search params and returns as object
   */
  const getFiltersFromParams = React.useCallback((): Record<
    string,
    string[] | string | number | boolean | number[]
  > => {
    const filters: Record<
      string,
      string[] | string | number | boolean | number[]
    > = {};

    // First, collect all array filters (e.g., filters[tags][]=value)
    const arrayFilters: Record<string, string[]> = {};
    searchParams.forEach((value, key) => {
      const arrayMatch = key.match(/^filters\[([^\]]+)\]\[\]$/);
      if (arrayMatch) {
        const filterKey = arrayMatch[1];
        if (!arrayFilters[filterKey]) {
          arrayFilters[filterKey] = [];
        }
        arrayFilters[filterKey].push(value);
      }
    });

    // Then, collect single value filters (e.g., filters[price]=value)
    searchParams.forEach((value, key) => {
      const singleMatch = key.match(/^filters\[([^\]]+)\]$/);
      if (singleMatch && !key.endsWith('[]')) {
        const filterKey = singleMatch[1];
        // Only set if not already set as array
        if (!arrayFilters[filterKey]) {
          // Try to parse as number if it looks like a number
          const numValue = Number(value);
          if (!Number.isNaN(numValue) && value.trim() !== '') {
            filters[filterKey] = numValue;
          } else if (value === 'true' || value === 'false') {
            filters[filterKey] = value === 'true';
          } else {
            filters[filterKey] = value;
          }
        }
      }
    });

    // Merge array filters
    Object.keys(arrayFilters).forEach((key) => {
      filters[key] = arrayFilters[key];
    });

    return filters;
  }, [searchParams]);

  /**
   * Gets all URL params as an object
   */
  const getAllParams = React.useCallback((): Record<string, unknown> => {
    const params: Record<string, unknown> = {};

    searchParams.forEach((value, key) => {
      // Skip filter params (they're handled separately)
      if (!key.startsWith('filters[')) {
        // Try to parse as number
        const numValue = Number(value);
        if (!Number.isNaN(numValue) && value.trim() !== '') {
          params[key] = numValue;
        } else if (value === 'true' || value === 'false') {
          params[key] = value === 'true';
        } else {
          params[key] = value;
        }
      }
    });

    // Add filters separately
    const filters = getFiltersFromParams();
    if (Object.keys(filters).length > 0) {
      params.filters = filters;
    }

    return params;
  }, [searchParams, getFiltersFromParams]);

  return { searchParams, updateParams, getFiltersFromParams, getAllParams };
};
